import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { dashboardApi, formatApiErrorDetail } from '../../lib/api';
import { formatCurrency, getAttachRateColor, getAttachRateBg } from '../../lib/utils';
import { AppLayout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import {
  Building2,
  ShieldCheck,
  TrendingUp,
  AlertTriangle,
  Receipt,
  ArrowRight,
  Loader2,
  Clock,
  CheckCircle,
} from 'lucide-react';
import { toast } from 'sonner';

export default function SuperAdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data: dashboardData } = await dashboardApi.superAdmin();
        setData(dashboardData);
      } catch (error) {
        toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#005DE8]" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
            Dashboard
          </h1>
          <p className="text-slate-600 mt-1">Platform overview and key metrics</p>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Tenants */}
          <Card className="border-slate-200 animate-slide-up stagger-1" data-testid="kpi-tenants">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500 uppercase tracking-wide">Total Tenants</p>
                  <p className="text-3xl font-bold text-slate-900 mt-2" style={{ fontFamily: 'Outfit' }}>
                    {data?.tenants?.total || 0}
                  </p>
                  <div className="flex items-center gap-3 mt-2 text-sm">
                    <span className="flex items-center text-emerald-600">
                      <CheckCircle className="h-3.5 w-3.5 mr-1" />
                      {data?.tenants?.active || 0} Active
                    </span>
                    <span className="flex items-center text-amber-600">
                      <Clock className="h-3.5 w-3.5 mr-1" />
                      {data?.tenants?.pending || 0} Pending
                    </span>
                  </div>
                </div>
                <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
                  <Building2 className="h-6 w-6 text-[#005DE8]" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Warranties */}
          <Card className="border-slate-200 animate-slide-up stagger-2" data-testid="kpi-warranties">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500 uppercase tracking-wide">Warranties Sold</p>
                  <p className="text-3xl font-bold text-slate-900 mt-2" style={{ fontFamily: 'Outfit' }}>
                    {data?.warranties?.this_month || 0}
                  </p>
                  <p className="text-sm text-slate-500 mt-2">
                    All time: {data?.warranties?.all_time || 0}
                  </p>
                </div>
                <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center">
                  <ShieldCheck className="h-6 w-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Attach Rate */}
          <Card className="border-slate-200 animate-slide-up stagger-3" data-testid="kpi-attach-rate">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-500 uppercase tracking-wide">Attach Rate</p>
                  <p className={`text-3xl font-bold mt-2 ${getAttachRateColor(data?.attach_rate || 0)}`} style={{ fontFamily: 'Outfit' }}>
                    {data?.attach_rate || 0}%
                  </p>
                  <div className="mt-3">
                    <Progress 
                      value={data?.attach_rate || 0} 
                      className="h-2"
                      indicatorClassName={getAttachRateBg(data?.attach_rate || 0)}
                    />
                  </div>
                </div>
                <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Invoices */}
          <Card className="border-slate-200 animate-slide-up stagger-4" data-testid="kpi-invoices">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500 uppercase tracking-wide">Invoice Status</p>
                  <div className="flex items-center gap-4 mt-3">
                    <div>
                      <p className="text-2xl font-bold text-emerald-600" style={{ fontFamily: 'Outfit' }}>
                        {data?.invoices?.paid || 0}
                      </p>
                      <p className="text-xs text-slate-500">Paid</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-red-600" style={{ fontFamily: 'Outfit' }}>
                        {data?.invoices?.overdue || 0}
                      </p>
                      <p className="text-xs text-slate-500">Overdue</p>
                    </div>
                  </div>
                </div>
                <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center">
                  <Receipt className="h-6 w-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Middle Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Performing Tenants */}
          <Card className="border-slate-200" data-testid="top-tenants-card">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>
                  Top Performing Tenants
                </CardTitle>
                <Link to="/admin/tenants">
                  <Button variant="ghost" size="sm" className="text-[#005DE8]">
                    View All
                    <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              {data?.top_tenants?.length > 0 ? (
                <div className="space-y-3">
                  {data.top_tenants.map((tenant, index) => (
                    <div
                      key={tenant.tenant_id}
                      className="flex items-center justify-between p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#005DE8] flex items-center justify-center text-white font-medium text-sm">
                          {index + 1}
                        </div>
                        <span className="font-medium text-slate-900">{tenant.company_name}</span>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-slate-900">{tenant.warranties_sold}</p>
                        <p className="text-xs text-slate-500">warranties</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  No warranty sales data yet
                </div>
              )}
            </CardContent>
          </Card>

          {/* Compliance Alerts */}
          <Card className="border-slate-200" data-testid="compliance-alerts-card">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-semibold flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                  <AlertTriangle className="h-5 w-5 text-red-500" />
                  Compliance Alerts
                </CardTitle>
                <Link to="/admin/compliance">
                  <Button variant="ghost" size="sm" className="text-[#005DE8]">
                    View All
                    <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                </Link>
              </div>
              <p className="text-sm text-slate-500">Exclusive contract tenants with attach rate below 80%</p>
            </CardHeader>
            <CardContent>
              {data?.compliance_alerts?.length > 0 ? (
                <div className="space-y-3">
                  {data.compliance_alerts.map((alert) => (
                    <div
                      key={alert.tenant_id}
                      className="flex items-center justify-between p-3 rounded-lg bg-red-50 border border-red-100"
                    >
                      <div>
                        <p className="font-medium text-slate-900">{alert.company_name}</p>
                        <p className="text-xs text-slate-500">Exclusive Contract</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-red-600">{alert.attach_rate}%</p>
                        <p className="text-xs text-red-500">attach rate</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-emerald-600">
                  <CheckCircle className="h-8 w-8 mx-auto mb-2" />
                  All exclusive tenants are compliant
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>
              Quick Actions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              <Link to="/admin/tenants">
                <Button variant="outline" data-testid="action-manage-tenants">
                  <Building2 className="h-4 w-4 mr-2" />
                  Manage Tenants
                </Button>
              </Link>
              <Link to="/admin/warranty-plans">
                <Button variant="outline" data-testid="action-warranty-plans">
                  <ShieldCheck className="h-4 w-4 mr-2" />
                  Warranty Plans
                </Button>
              </Link>
              <Link to="/admin/invoices">
                <Button variant="outline" data-testid="action-generate-invoice">
                  <Receipt className="h-4 w-4 mr-2" />
                  Generate Invoice
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
